import { TicketApprovalRequired } from './TicketApprovalRequired';
import { TicketApproved } from './TicketApproved';
import { TicketDataResponse, TicketSuccessResponse } from './TicketSlideIn';
import { TicketSuccessDetails } from './TicketSuccessDetails';
import {
    portalSlideIn,
    SlideInPortalId,
} from '../../account/root/components/PortalSlidein';
import { type JSX, useState } from 'react';
import {
    RequestStatus,
    ServerRequestState,
} from 'dg-web-shared/lib/hooks/ServerStateHooks';
import { ParkingpayAsyncLoadedSection } from '../../common/components/ParkingpayAsyncLoadedSection';
import { TicketError } from './ParkCreateTicketPlus';
import { TransactionSlideIn } from '../components/ParkVariantSelection';
import {
    TicketPlusTitle,
    ZoneInfoSlideInTitle,
} from '../../common/components/ticketplus/TicketPlusGenericTexts';
import { PayStationPaymentReceiptView } from '../../common/components/ticketplus/PayStationPaymentReceiptView.tsx';
import { QuickCheckoutLayout } from '../../quickcheckout/common/QuickCheckoutLayout.tsx';
import { EntryPlusState, TicketReceipt } from 'dg-web-shared/model/Ticket.ts';

export function SharedTicketSlideIn({
    portal,
    onClose,
    ticketState,
    refetchTicketState,
    onTicketReset,
    onTicketApproveSuccess,
    renderError,
}: {
    portal: SlideInPortalId;
    onClose: () => void;
    ticketState: ServerRequestState<TicketSuccessResponse, TicketError>;
    refetchTicketState: () => void;
    onTicketReset: () => void;
    onTicketApproveSuccess: () => void;
    renderError?: (error: TicketError | null) => JSX.Element | null;
}) {
    const [openZoneInfoSlideIn, setOpenZoneInfoSlideIn] = useState(false);
    return (
        <>
            <ParkingpayAsyncLoadedSection
                state={ticketState}
                renderSuccess={ticketData => {
                    if (ticketData.hasPayStationReceipt) {
                        return (
                            <TicketReceiptSlideIn
                                title={<TicketPlusTitle />}
                                open
                                onClose={onClose}
                                ticketReceipt={ticketData.ticketReceipt}
                                hasPaidPayment={ticketData.hasPaidPayment}
                                hasDownloadableReceipt={
                                    ticketData.hasDownloadableReceipt
                                }
                                showTicketPlusUnavailableMessage={
                                    portal === SlideInPortalId.PARK_CREATE
                                }
                                portal={SlideInPortalId.USER_ACCOUNT}
                            />
                        );
                    }
                    return (
                        <>
                            <TicketFlowSlideIn
                                portal={portal}
                                open
                                onClose={onClose}
                                title={<TicketPlusTitle />}
                                headerColor={
                                    ticketState.status ===
                                        RequestStatus.SUCCESS &&
                                    ticketData.ticketAndState.state ===
                                        EntryPlusState.BOOKED
                                        ? 'lightBlue'
                                        : 'darkBlue'
                                }
                                ticketData={ticketData}
                                refetchTicketState={refetchTicketState}
                                onTicketReset={onTicketReset}
                                onTicketApproveSuccess={onTicketApproveSuccess}
                                openZoneInfo={() =>
                                    setOpenZoneInfoSlideIn(true)
                                }
                            />
                            <TransactionSlideIn
                                headerColor="darkBlue"
                                title={<ZoneInfoSlideInTitle />}
                                onClose={() => setOpenZoneInfoSlideIn(false)}
                                portal={portal}
                                open={openZoneInfoSlideIn}
                                selectedZone={ticketData.zone}
                                isOffstreet={true}
                            />
                        </>
                    );
                }}
                renderError={renderError}
                portal={portal}
            />
        </>
    );
}

const TicketReceiptSlideIn =
    portalSlideIn<TicketReceiptProps>(TicketReceiptContent);

type TicketReceiptProps = {
    ticketReceipt: TicketReceipt;
    hasPaidPayment: boolean;
    hasDownloadableReceipt: boolean;
    showTicketPlusUnavailableMessage: boolean;
};

function TicketReceiptContent({
    ticketReceipt,
    hasPaidPayment,
    hasDownloadableReceipt,
    showTicketPlusUnavailableMessage,
}: TicketReceiptProps) {
    return (
        <QuickCheckoutLayout>
            <PayStationPaymentReceiptView
                ticketReceipt={ticketReceipt}
                hasPaidPayment={hasPaidPayment}
                hasDownloadableReceipt={hasDownloadableReceipt}
                showTicketPlusUnavailableMessage={
                    showTicketPlusUnavailableMessage
                }
            />
        </QuickCheckoutLayout>
    );
}

const TicketFlowSlideIn = portalSlideIn<TicketFlowProps>(TicketFlowContent);

type TicketFlowProps = {
    ticketData: TicketDataResponse;
    refetchTicketState: () => void;
    onTicketReset: () => void;
    onTicketApproveSuccess: () => void;
    openZoneInfo: () => void;
};

function TicketFlowContent({
    ticketData,
    refetchTicketState,
    onTicketReset,
    onTicketApproveSuccess,
    openZoneInfo,
}: TicketFlowProps) {
    switch (ticketData.ticketAndState.state) {
        case EntryPlusState.CREATED:
        case EntryPlusState.SCANNED:
        case EntryPlusState.ALIAS_PENDING:
            return (
                <TicketApprovalRequired
                    ticketData={ticketData}
                    refetchTicketState={refetchTicketState}
                    onTicketApproveSuccess={onTicketApproveSuccess}
                    openZoneInfo={openZoneInfo}
                />
            );
        case EntryPlusState.APPROVED:
            return (
                <TicketApproved
                    ticketData={ticketData}
                    onTicketReset={onTicketReset}
                    refetchTicketState={refetchTicketState}
                    openZoneInfo={openZoneInfo}
                />
            );
        case EntryPlusState.BOOKED:
            return <TicketSuccessDetails ticketData={ticketData} />;
    }
}
